import {
  FlexColumn,
  FlexRowCenterBoth,
  FlexRowCenterVertical,
  GenericMessageDescriptor,
  Icon,
  type IconType,
  StyleUtils,
  useFormatMessageGeneric,
} from '@main/core-ui';
import React, { useState } from 'react';

import { CODE_FONT_STYLE, CodeBlock } from '../CodeBlock';
import {
  CalloutBackground,
  CalloutLevel,
  CodeBlockWrapper,
  StyledAccordionContent,
  StyledAccordionHeader,
  StyledAccordionItem,
  StyledAccordionRoot,
  StyledAccordionTrigger,
} from './wrappers';

export interface CalloutProps {
  /** Title text */
  title?: GenericMessageDescriptor;
  /** The "log level" indicating color */
  level: CalloutLevel;
  /**
   * The callout message
   *
   * ```md
   * Supports limited Markdown:
   * - Inline code with backticks: `code`
   * - Links with markdown syntax: [link text](url)
   * ```
   */
  message?: string;
  /** If you have logs from an upstream service like Sombra or an integration, pass them here. */
  upstreamLogs?: {
    /** Accordion header for this log, e.g., "View the upstream error we received from Salesforce" */
    header: GenericMessageDescriptor;
    /** The raw upstream logs */
    message: string;
  }[];
  /** Whether the callout is dismissable */
  dismissable?:
    | boolean
    | {
        /** Callback when the callout is dismissed */
        onDismiss?: () => void;
      };
  /** A slot for additional content at the bottom of the callout */
  footer?: React.ReactNode;
}

const defaultIcons: Record<CalloutLevel, IconType> = {
  info: 'i-circle',
  success: 'checkmark-circle',
  warning: 'exclamation-triangle',
  error: 'exclamation-triangle',
};

/**
 * Converts markdown formatted text into React components
 */
export const parseMarkdown = (text: string): React.ReactNode => {
  if (!text) return text;

  // Step 1: Process inline code segments (text between backticks)
  const codeRegex = /`([^`]+)`/g;
  let lastIndex = 0;
  const segments: React.ReactNode[] = [];

  let codeMatch = codeRegex.exec(text);
  while (codeMatch !== null) {
    // Add text before the code segment
    if (codeMatch.index > lastIndex) {
      segments.push(text.substring(lastIndex, codeMatch.index));
    }

    // Add the code segment wrapped in a code tag
    segments.push(
      <code
        key={`code-${codeMatch.index}`}
        style={{ ...CODE_FONT_STYLE, color: 'inherit' }}
      >
        {codeMatch[1]}
      </code>,
    );

    lastIndex = codeMatch.index + codeMatch[0].length;
    codeMatch = codeRegex.exec(text);
  }

  // Add any remaining text after the last code segment
  if (lastIndex < text.length) {
    segments.push(text.substring(lastIndex));
  }

  // Step 2: Process links in each text segment
  return segments
    .map((segment, segmentIndex) => {
      if (typeof segment !== 'string') {
        return segment; // Already a React element (code)
      }

      // Process markdown links
      const linkRegex = /\[(.*?)\]\((.*?)\)/;
      const parts = segment.split(/(\[.*?\]\(.*?\))/);

      return parts.map((part, partIndex) => {
        const linkMatch = part.match(linkRegex);
        if (linkMatch) {
          const [, linkText, url] = linkMatch;
          return (
            <a
              key={`${segmentIndex}-link-${partIndex}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                fontWeight: 500,
              }}
            >
              {linkText}
            </a>
          );
        }
        return part;
      });
    })
    .flat();
};

/**
 * Modern callout component (published April 2025)
 *
 * @see https://www.figma.com/design/UyzZinoqcLrQSf2gtEbfkK/Transcend%3A-UI-Design-*?node-id=9882-7650&t=8viv8TYhfNdyMv7N-1
 */
export const Callout: React.FC<CalloutProps> = ({
  title,
  level,
  message,
  upstreamLogs,
  dismissable,
  footer,
}) => {
  const { formatMessageGeneric } = useFormatMessageGeneric();
  const [isOpen, setIsOpen] = useState(true);

  const icon = defaultIcons[level];

  if (!isOpen) return null;

  return (
    <CalloutBackground level={level}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          padding: '10px',
        }}
      >
        {/* 2-column grid. Be careful about adding elements to this level! */}
        {/* First row */}
        <FlexRowCenterBoth style={{ marginRight: '10px' }}>
          <Icon type={icon} strokeWidth="2px" />
        </FlexRowCenterBoth>
        <FlexRowCenterVertical style={{ justifyContent: 'space-between' }}>
          <p
            style={{
              fontWeight: 600,
              flexGrow: 1,
              margin: 0,
              lineHeight: 1,
            }}
          >
            {title && formatMessageGeneric(title)}
          </p>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {dismissable && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  if (
                    typeof dismissable === 'object' &&
                    'onDismiss' in dismissable
                  ) {
                    dismissable.onDismiss?.();
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'inherit',
                  opacity: 0.7,
                }}
                aria-label="Dismiss callout"
              >
                <Icon type="x-no-padding" size="0.8em" />
              </button>
            )}
          </div>
        </FlexRowCenterVertical>
        {/* Second row */}
        <div /> {/* empty grid cell below the icon */}
        <FlexColumn
          style={{
            marginTop: StyleUtils.Spacing.sm,
            gap: StyleUtils.Spacing.sm,
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* The structured message printed as one paragraph (with Markdown replaced) */}
          {message && <p style={{ margin: 0 }}>{parseMarkdown(message)}</p>}
          {/* Upstream error accordion */}
          {upstreamLogs && (
            <StyledAccordionRoot type="multiple">
              {upstreamLogs.map((upstreamLog) => {
                let { message } = upstreamLog;
                let isJson = false;
                try {
                  // Attempt to format the message as JSON
                  message = JSON.stringify(JSON.parse(message), null, 2);
                  isJson = true;
                } catch {
                  // If it's not JSON, keep the original message
                }

                return (
                  <StyledAccordionItem
                    key={upstreamLog.message}
                    value={upstreamLog.message}
                  >
                    <StyledAccordionHeader>
                      <StyledAccordionTrigger>
                        <Icon
                          type="caret-right"
                          size="1em"
                          // color={messageColor}
                        />
                        {formatMessageGeneric(upstreamLog.header)}
                      </StyledAccordionTrigger>
                    </StyledAccordionHeader>
                    <StyledAccordionContent>
                      <CodeBlockWrapper>
                        <CodeBlock
                          snippet={message}
                          language={isJson ? 'json' : 'text'}
                          wrap={!isJson}
                        >
                          {message}
                        </CodeBlock>
                      </CodeBlockWrapper>
                    </StyledAccordionContent>
                  </StyledAccordionItem>
                );
              })}
            </StyledAccordionRoot>
          )}
          {footer}
        </FlexColumn>
      </div>
    </CalloutBackground>
  );
};
